import { KEYCLOAK_REALM, KEYCLOAK_HOST } from '@/config/constants';
import KeycloakAdmin from '@keycloak/keycloak-admin-client';
import UserRepresentation from '@keycloak/keycloak-admin-client/lib/defs/userRepresentation';
import { UserQuery } from '@keycloak/keycloak-admin-client/lib/resources/users';
import moment from 'moment';
import { SamsungUserDetails } from '../auth/type';
import {
  KC_ROLE,
  KC_STATE,
  KC_STATE_STATUS,
  KeycloakUserDetails,
  ManufacturerAdminData,
  ROLE_LIST_NAME,
  UsedManufacturerMapping,
  UserManagementData,
  UserRegisterParams,
} from './type';
import * as utils from '@/components/utils';
import { AuthCredential, MakeDictionary } from 'swuif';
import { UserManagementField } from '@/routes/Main/UserManagement/form/UserManagementForm';
import { sbaas_mdr_api, sbaas } from '@/config/sbaas';

export const getAccessToken = async (
  grantType: 'client_credentials' | 'password' | 'refresh_token',
  data: {
    userData?: SamsungUserDetails;
    refreshToken?: string;
  },
) => {
  const api = await sbaas_mdr_api.script('kc_token').post({
    body: {
      grantType: grantType,
      username: data.userData ? data.userData.preferred_username : '',
      refreshToken: data.refreshToken,
    },
  });
  const aToken = api.data;
  if (aToken.access_token) {
    utils.setStorage(
      'KC_DT',
      moment()
        .toDate()
        .getTime()
        .toString(),
    );
    utils.setStorage('KC_AT', aToken.access_token);
    utils.setStorage('KC_RT', aToken.refresh_token || '');
    return aToken;
  } else return Promise.reject(aToken);
};

export const getUserDetails = async (accessToken: string) => {
  const res = await sbaas_mdr_api.script('kc_introspect').post({
    body: {
      token: accessToken,
    },
  });
  return res.data as KeycloakUserDetails;
};

//---------------Keycloak Admin----------------------//

export const getKcAdmin = () => {
  const kcAdmin = new KeycloakAdmin({
    baseUrl: KEYCLOAK_HOST,
    realmName: KEYCLOAK_REALM,
  });
  return kcAdmin;
};

export const setKcAdminToken = async (kcAdmin?: KeycloakAdmin) => {
  let resultToken: string = '';
  const api = await sbaas_mdr_api.script('kc_token_v2').post({
    body: {
      ...utils.generateSbaasKey('Admin', 'Admin'),
    },
  });
  const aToken = api.data;
  if (aToken.access_token) {
    if (kcAdmin) kcAdmin.setAccessToken(aToken.access_token);
    return resultToken;
  } else return Promise.reject(aToken);
};

export const setKcAdminTokenActivation = async (kcAdmin?: KeycloakAdmin) => {
  let resultToken: string = '';
  const api = await sbaas.script('kc_token_v4').post({
    body: {
      ...utils.generateSbaasKey('Admin', 'Admin'),
    },
  });
  const aToken = api.data;
  if (aToken.access_token) {
    if (kcAdmin) kcAdmin.setAccessToken(aToken.access_token);
    return resultToken;
  } else return Promise.reject(aToken);
};

export const getUserById = async (userId: string) => {
  const kcAdmin = getKcAdmin();
  await setKcAdminToken(kcAdmin);
  const userData = await kcAdmin.users.findOne({ id: userId });
  return userData;
};

export const getUserByIdActivation = async (userId: string) => {
  const kcAdmin = getKcAdmin();
  await setKcAdminTokenActivation(kcAdmin);
  const userData = await kcAdmin.users.findOne({ id: userId });
  return userData;
};

// export const getUserRole = async (query: UserQuery) => {
//   const kcAdmin = getKcAdmin();
//   await setKcAdminToken(kcAdmin);
//   const userData = await kcAdmin.users.find(query);
//   if (
//     userData.length > 0 &&
//     userData[0].attributes &&
//     userData[0].attributes['role']
//   ) {
//     return userData[0].attributes['role'][0] as string;
//   }
//   return '';
// };

export const validateUserRole = async (query: UserQuery) => {
  const res = await sbaas.script('validate_user_role').post({
    body: { query: query, ...utils.generateSbaasKey('User', 'Register') },
  });
  return res.data;
};

export const activateUser = async (userId: string) => {
  const kcAdmin = getKcAdmin();
  await setKcAdminTokenActivation(kcAdmin);
  const user = await kcAdmin.users.findOne({ id: userId });
  if (user !== undefined) {
    user.emailVerified = true;
    if (!user.attributes) user.attributes = {};
    user.attributes['state'] = KC_STATE.ACTIVE || '';
    await kcAdmin.users.update({ id: userId }, user);
  }
};

export const updateUserStatus = async (
  userId: string,
  status: string,
  reason?: string,
) => {
  const kcAdmin = getKcAdmin();
  await setKcAdminToken(kcAdmin);
  const userData = await kcAdmin.users.findOne({ id: userId });
  if (userData !== undefined) {
    const payload: UserRepresentation = { ...userData };
    payload.attributes = { ...userData.attributes };
    if (reason) payload.attributes[`reason_to_${status}`] = reason;
    else if (status === 'active')
      delete payload.attributes['reason_to_inactive'];
    switch (status) {
      case 'reject':
        payload.attributes['state'] = KC_STATE.REJECTED;
        break;
      case 'inactive':
        payload.attributes['state'] = KC_STATE.DISABLED;
        break;
      case 'delete':
        payload.attributes['state'] = KC_STATE.DELETED;
        payload.emailVerified = false;
        payload.attributes['role'] = 'DELETED';
        break;
      case 'active':
        payload.enabled = true;
        payload.attributes['state'] = KC_STATE.ACTIVE;
        break;
    }
    await kcAdmin.users.update({ id: userId }, payload);
  }
};

export const updateUserName = async (saData: SamsungUserDetails) => {
  if (saData['update_name']) {
    const kcAdmin = getKcAdmin();
    await setKcAdminToken(kcAdmin);
    const user = await kcAdmin.users.findOne({ id: saData.sub || '' });
    if (user !== undefined) {
      user.firstName = saData.given_name;
      user.lastName = saData.family_name;
      if (user.attributes) {
        delete user.attributes['update_name'];
      }
      kcAdmin.users.update({ id: user.id || '' }, user);
    }
  }
};

export const checkAvailableUser = async (username: string, email: string) => {
  const kcAdmin = getKcAdmin();
  await setKcAdminToken(kcAdmin);
  const userData = await kcAdmin.users.find({ username: username });
  const userData2 = await kcAdmin.users.find({ email: email });
  if (userData.length > 0 || userData2.length > 0) {
    const user = userData[0] || userData2[0];
    if (user.enabled || user.emailVerified) {
      return {
        state: false,
        id: user.id,
      };
    } else {
      return {
        state: true,
        id: user.id,
      };
    }
  }
  return {
    state: true,
    id: undefined,
  };
};

export const registerUser = async (
  data: UserRegisterParams,
  userInit: SamsungUserDetails,
) => {
  const kcAdmin = getKcAdmin();
  await setKcAdminToken(kcAdmin);
  const payload: UserRepresentation = {
    username: data.username,
    email:
      data.role === 'Manufacturer Admin' ? data.email : userInit.email || '',
    firstName: userInit.given_name || '',
    lastName: userInit.family_name || '',
    enabled: true,
    attributes: {
      preferred_role: KC_ROLE[data.role || ''] || '',
      preferred_mnid: data.role === 'Manufacturer Admin' ? data.mnId : '',
      pic_email: data.role === 'Partner User' ? data.email : '',
      purpose_to_use: data.purpose,
      division: data.division,
      state: KC_STATE.PENDING,
    },
  };
  try {
    await kcAdmin.users.update(
      {
        id: userInit.sub || '',
      },
      payload,
    );
    return {
      id: userInit.sub || '',
    };
  } catch (error) {
    const err: any = error;
    return Promise.reject(err.response.data);
  }
};

export const upsertUser = async (
  data: MakeDictionary<Partial<UserManagementField>>,
  userId?: string,
  isCreate: boolean = false,
  isApprove: boolean = false,
) => {
  const kcAdmin = getKcAdmin();
  await setKcAdminToken(kcAdmin);
  const payload: UserRepresentation = {
    username: data.username,
    email: data.role === KC_ROLE['Partner User'] ? data.username : data.saEmail,
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    enabled: true,
    attributes: {
      role: data.role,
      mnid: data.role === KC_ROLE['Super Admin'] ? ['ALL'] : data.mnid || [],
      pic_email:
        data.role === KC_ROLE['Partner User'] ? data.picEmail : undefined,
      purpose_to_use: data.purposeUse,
      reason_to_inactive: !data.status ? data.reasonInactive : undefined,
      update_name: isCreate ? true : undefined,
      division: data.division,
      state: isApprove
        ? KC_STATE.APPROVED
        : data.status
        ? KC_STATE.ACTIVE
        : KC_STATE.DISABLED,
    },
  };

  if (userId) {
    try {
      await kcAdmin.users.update(
        {
          id: userId,
        },
        payload,
      );
      return {
        id: userId,
      };
    } catch (error) {
      const err: any = error;
      return Promise.reject(err);
    }
  } else {
    try {
      const userIds = await kcAdmin.users.create(payload);
      return userIds;
    } catch (error) {
      const err: any = error;
      return Promise.reject(err);
    }
  }
};

export const addUserWithRole = async (userId: string, role: string) => {
  const kcAdmin = getKcAdmin();
  await setKcAdminToken(kcAdmin);
  const rData = await kcAdmin.roles.findOneByName({ name: role });
  if (rData && rData.id && rData.name) {
    await kcAdmin.users.addRealmRoleMappings({
      id: userId,
      roles: [{ id: rData.id, name: rData.name }],
    });
  }
};

export const delUserRole = async (userId: string, role: string) => {
  const kcAdmin = getKcAdmin();
  await setKcAdminToken(kcAdmin);
  const rData = await kcAdmin.roles.findOneByName({ name: role });
  if (rData && rData.id && rData.name) {
    await kcAdmin.users.delRealmRoleMappings({
      id: userId,
      roles: [{ id: rData.id, name: rData.name }],
    });
  }
};

export const generateUserList = (
  users: UserRepresentation[],
  credential: AuthCredential,
) => {
  generateAdminData(users);
  generateUsedManufacture(users);
  const mnId: string[] = credential.user.mnId || [];
  const saEmail: string = credential.user.email || '';
  const userRole: string = credential.user.keycloakRole || '';
  let uData: UserManagementData[] = [];
  for (let i in users) {
    if (users.hasOwnProperty(i)) {
      let dat: UserManagementData = {
        ...users[i],
        role: '',
        status: 'pending',
      };
      if (
        dat.attributes &&
        dat.attributes.role &&
        dat.attributes.role.length > 0
      ) {
        if (
          [KC_STATE.ACTIVE, KC_STATE.APPROVED, KC_STATE.DISABLED].indexOf(
            (dat.attributes['state'] || []).toString(),
          ) > -1
        ) {
          if (ROLE_LIST_NAME[dat.attributes.role[0]]) {
            const uRole: string = ROLE_LIST_NAME[dat.attributes.role[0]];
            dat.role = uRole;
            dat.status =
              KC_STATE_STATUS[(dat.attributes['state'] || []).toString()];

            if (userRole === 'MANUFACTURER_ADMIN') {
              if (uRole === 'Partner User') {
                if (
                  dat.attributes.pic_email &&
                  dat.attributes.pic_email.length > 0 &&
                  dat.attributes.pic_email.indexOf(saEmail) > -1
                ) {
                  uData.push(dat);
                }
              }
              if (uRole === 'Manufacturer Admin') {
                if (dat.attributes.mnid && dat.attributes.mnid.length > 0) {
                  for (let id of mnId) {
                    if (dat.attributes.mnid.indexOf(id) > -1) {
                      uData.push(dat);
                      break;
                    }
                  }
                }
              }
            } else if (userRole === 'SUPERADMIN') {
              uData.push(dat);
            }
          }
        } // eslint-disable-next-line
      } else if (
        dat.attributes &&
        (dat.attributes['state'] &&
          (dat.attributes['state'] === KC_STATE.PENDING ||
            (dat.attributes['state']['0'] &&
              dat.attributes['state']['0'] === KC_STATE.PENDING)))
      ) {
        if (userRole === 'MANUFACTURER_ADMIN') {
          if (
            dat.attributes.preferred_role &&
            dat.attributes.pic_email &&
            dat.attributes.preferred_role.length > 0 &&
            dat.attributes.pic_email.length > 0 &&
            dat.attributes.preferred_role.indexOf('PARTNER_USER') > -1 &&
            dat.attributes.pic_email.indexOf(saEmail) > -1
          ) {
            uData.push(dat);
          }
        } else if (userRole === 'SUPERADMIN') {
          if (
            dat.attributes.preferred_role &&
            dat.attributes.preferred_role.length > 0 &&
            dat.attributes.preferred_role.indexOf('MANUFACTURER_ADMIN') > -1
          ) {
            uData.push(dat);
          }
        }
      }
    }
  }
  return uData;
};

export const generateAdminData = (users: UserRepresentation[]) => {
  let adminData: ManufacturerAdminData[] = [];
  for (let user of users) {
    if (
      user.attributes &&
      user.attributes.role &&
      user.attributes.role.length > 0 &&
      user.attributes.role[0] === KC_ROLE['Manufacturer Admin'] &&
      (user.attributes['state'] || []).toString() === KC_STATE.ACTIVE
    ) {
      const uData: ManufacturerAdminData = {
        email: user.email || '',
        mnid: user.attributes.mnid || [],
      };
      adminData.push(uData);
    }
  }
  utils.setStorage('MN_DATA', JSON.stringify(adminData));
  return adminData;
};

export const generateUsedManufacture = (users: UserRepresentation[]) => {
  let usedMn: UsedManufacturerMapping = {};
  for (let user of users) {
    if (
      user.attributes &&
      user.attributes.role &&
      user.attributes.role.length > 0 &&
      user.attributes.role[0] === KC_ROLE['Partner User'] &&
      user.attributes.pic_email &&
      user.attributes.pic_email.length > 0 &&
      user.attributes.mnid &&
      user.attributes.mnid.length > 0 &&
      [KC_STATE.ACTIVE, KC_STATE.APPROVED, KC_STATE.DISABLED].indexOf(
        (user.attributes['state'] || []).toString(),
      ) > -1
    ) {
      const picEmail: string = user.attributes.pic_email[0];
      let temp: ManufacturerAdminData = usedMn[picEmail] || {
        email: picEmail,
        mnid: [],
      };
      for (let mn of user.attributes.mnid) {
        if (temp.mnid.indexOf(mn) < 0) temp.mnid.push(mn);
      }
      usedMn[picEmail] = { ...temp };
    }
  }
  utils.setStorage('MN_USED', JSON.stringify(usedMn));
  return usedMn;
};

export const getMnAdminData = async () => {
  if (utils.getStorage('MN_DATA')) {
    return JSON.parse(utils.getStorage('MN_DATA')) as ManufacturerAdminData[];
  } else {
    const kcAdmin = getKcAdmin();
    await setKcAdminToken(kcAdmin);
    const uData = await kcAdmin.users.find({
      email: '@samsung.com',
    });
    return generateAdminData(uData);
  }
};

export const getUsedMnId = async () => {
  if (utils.getStorage('MN_USED')) {
    return JSON.parse(utils.getStorage('MN_USED')) as UsedManufacturerMapping;
  } else {
    const kcAdmin = getKcAdmin();
    await setKcAdminToken(kcAdmin);
    const uData = await kcAdmin.users.find();
    return generateUsedManufacture(uData);
  }
};
